import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

export async function loadGltfFromArrayBuffer(arrayBuffer, path = 'model.glb') {
  const loader = new GLTFLoader()

  return await new Promise((resolve, reject) => {
    loader.parse(arrayBuffer, path, resolve, reject)
  })
}

export function inspectGltfGeometry(gltf) {
  gltf.scene.updateWorldMatrix(true, true)

  const primitives = []
  const associations = gltf.parser?.associations
  gltf.scene.traverse(node => {
    if (!node.isMesh) return

    const nodeAssociation = associations?.get(node) || {}
    const geometries = Array.isArray(node.geometry) ? node.geometry : [node.geometry]
    const materials = Array.isArray(node.material) ? node.material : [node.material]

    for (const [primitiveIndex, geometry] of geometries.entries()) {
      if (!geometry?.isBufferGeometry) continue

      geometry.computeBoundingBox()
      const material = materials[primitiveIndex] || materials[0] || null
      const materialAssociation = material ? associations?.get(material) || {} : {}
      const localBoundingBox = geometry.boundingBox?.clone() || new THREE.Box3()
      const worldBoundingBox = localBoundingBox.clone().applyMatrix4(node.matrixWorld)
      const position = geometry.getAttribute('position')
      const normal = geometry.getAttribute('normal')
      const uv = geometry.getAttribute('uv')
      const index = geometry.getIndex()
      const vertexCount = position?.count ?? 0
      const indexCount = index?.count ?? 0
      const triangleCount = indexCount > 0 ? indexCount / 3 : vertexCount / 3

      primitives.push({
        nodeName: node.name || '',
        meshName: geometry.name || node.name || '',
        meshIndex: nodeAssociation.meshes ?? null,
        nodeIndex: nodeAssociation.nodes ?? null,
        primitiveIndex: nodeAssociation.primitives ?? primitiveIndex,
        vertexCount,
        indexCount,
        triangleCount,
        attributes: {
          POSITION: Boolean(position),
          NORMAL: Boolean(normal),
          TEXCOORD_0: Boolean(uv),
        },
        materialName: material?.name || '',
        materialIndex: materialAssociation.materials ?? null,
        localTransform: matrixToRows(node.matrix),
        worldTransform: matrixToRows(node.matrixWorld),
        boundingBox: {
          local: boxToObject(localBoundingBox),
          world: boxToObject(worldBoundingBox),
        },
        needsTransformBake: !node.matrixWorld.equals(new THREE.Matrix4()),
      })
    }
  })

  return primitives
}

export function formatGeometryReport(primitives) {
  return primitives.map(primitive => [
    `mesh/node: ${primitive.meshName || '(unnamed)'} / ${primitive.nodeName || '(unnamed)'}`,
    `gltf indices: mesh ${formatNullable(primitive.meshIndex)}, node ${formatNullable(primitive.nodeIndex)}, primitive ${primitive.primitiveIndex}`,
    `vertices: ${primitive.vertexCount}`,
    `indices: ${primitive.indexCount}`,
    `triangles: ${formatNumber(primitive.triangleCount)}`,
    `attributes: ${Object.entries(primitive.attributes).filter(([, present]) => present).map(([name]) => name).join(', ') || '(none)'}`,
    `material: ${primitive.materialName || '(unnamed)'} / ${formatNullable(primitive.materialIndex)}`,
    `local transform: ${JSON.stringify(primitive.localTransform)}`,
    `world transform: ${JSON.stringify(primitive.worldTransform)}`,
    `bounding box local: ${JSON.stringify(primitive.boundingBox.local)}`,
    `bounding box world: ${JSON.stringify(primitive.boundingBox.world)}`,
    `runtime bake needed: ${primitive.needsTransformBake ? 'yes' : 'no'}`,
  ].join('\n')).join('\n\n')
}

function matrixToRows(matrix) {
  const e = matrix.elements
  return [
    [e[0], e[4], e[8], e[12]].map(round),
    [e[1], e[5], e[9], e[13]].map(round),
    [e[2], e[6], e[10], e[14]].map(round),
    [e[3], e[7], e[11], e[15]].map(round),
  ]
}

function boxToObject(box) {
  return {
    min: vectorToObject(box.min),
    max: vectorToObject(box.max),
  }
}

function vectorToObject(vector) {
  return {
    x: round(vector.x),
    y: round(vector.y),
    z: round(vector.z),
  }
}

function round(value) {
  return Number.isFinite(value) ? Number(value.toFixed(6)) : value
}

function formatNumber(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(6)
}

function formatNullable(value) {
  return value == null ? '(unknown)' : String(value)
}
